import { useEffect, useRef, useState, type MutableRefObject } from 'react';

type TDestroyableGame = {
  destroy: () => void;
};

interface IUsePhaserGameLoaderOptions<TGame extends TDestroyableGame> {
  containerRef: MutableRefObject<globalThis.HTMLDivElement | null>;
  gameRef: MutableRefObject<TGame | null>;
  loadGame: (parent: globalThis.HTMLDivElement) => Promise<TGame>;
  onLoaded?: (game: TGame) => void;
}

export function usePhaserGameLoader<TGame extends TDestroyableGame>({
  containerRef,
  gameRef,
  loadGame,
  onLoaded,
}: IUsePhaserGameLoaderOptions<TGame>): { isReady: boolean } {
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(false);

    const parent = containerRef.current;
    if (!parent) {
      return;
    }

    let isCancelled = false;

    void (async () => {
      const game = await loadGame(parent);
      if (isCancelled || !containerRef.current) {
        game.destroy();
        return;
      }

      gameRef.current = game;
      setIsReady(true);
      onLoadedRef.current?.(game);
    })();

    return () => {
      isCancelled = true;
      if (gameRef.current) {
        gameRef.current.destroy();
        gameRef.current = null;
      }
    };
  }, [containerRef, gameRef, loadGame]);

  return { isReady };
}
